import React, { useCallback } from 'react';
import { Table, Button, Input, InputNumber, Select, Radio, Space, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { FN110, FN1122 } from '../../../services/n202Api';
import LookupField from '../../../components/LookupField';

interface Tab3Props {
  formData: Partial<FN110>;
  rows: Partial<FN1122>[];
  isEditing: boolean;
  onRowsChange: (updater: (prev: Partial<FN1122>[]) => Partial<FN1122>[]) => void;
  onVendorFieldChange: (key: keyof FN110, value: FN110[keyof FN110]) => void;
  isFieldDisabled: (key: string) => boolean;
}

const Tab3Vendor2Details: React.FC<Tab3Props> = ({
  formData, rows, isEditing, onRowsChange, onVendorFieldChange, isFieldDisabled,
}) => {
  const handleCellChange = useCallback((idx: number, key: keyof FN1122, value: FN1122[keyof FN1122]) => {
    onRowsChange(prev => prev.map((r, i) => i === idx ? { ...r, [key]: value } : r));
  }, [onRowsChange]);

  const handleAdd = useCallback(() => {
    const maxSeq = rows.reduce((max, r) => Math.max(max, r.fn1122_02 || 0), 0);
    onRowsChange(prev => [...prev, { fn1122_01: formData.fn110_01, fn1122_02: maxSeq + 1 }]);
  }, [rows, formData.fn110_01, onRowsChange]);

  const handleDelete = useCallback((idx: number) => {
    onRowsChange(prev => prev.filter((_, i) => i !== idx).map((r, i) => ({ ...r, fn1122_02: i + 1 })));
  }, [onRowsChange]);

  const columns: ColumnsType<Partial<FN1122>> = [
    { title: <span data-contract-field="fn1122_02">#</span>, dataIndex: 'fn1122_02', width: 50, render: (_v, _r, i) => i + 1 },
    {
      title: <span data-contract-field="fn1122_11">原因代碼</span>, dataIndex: 'fn1122_11', width: 120,
      render: (v, _r, i) => isEditing
        ? <Input size="small" value={v} onChange={e => handleCellChange(i, 'fn1122_11', e.target.value)} />
        : v,
    },
    {
      title: <span data-contract-field="fn1122_12">幣別</span>, dataIndex: 'fn1122_12',
      render: (v, _r, i) => isEditing
        ? <Input size="small" value={v} onChange={e => handleCellChange(i, 'fn1122_12', e.target.value)} />
        : v,
    },
    {
      title: <span data-contract-field="fn1122_13">匯率</span>, dataIndex: 'fn1122_13',
      render: (v, _r, i) => isEditing
        ? <InputNumber size="small" value={v} onChange={value => handleCellChange(i, 'fn1122_13', value || 0)} style={{ width: '100%' }} />
        : v,
    },
    {
      title: <span data-contract-field="fn1122_14">金額</span>, dataIndex: 'fn1122_14', width: 120, align: 'right' as const,
      render: (v, _r, i) => isEditing
        ? <InputNumber size="small" value={v} onChange={val => handleCellChange(i, 'fn1122_14', val || 0)} style={{ width: '100%' }} />
        : (v || 0).toLocaleString(),
    },
    ...(isEditing ? [{
      title: '操作', width: 60, align: 'center' as const,
      render: (_v: unknown, _r: Partial<FN1122>, i: number) => (
        <Popconfirm title="確認刪除?" onConfirm={() => handleDelete(i)}>
          <Button type="link" size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    }] : []),
  ];

  return (
    <div>
      {/* Vendor 2 header fields */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, auto 1fr)', gap: '8px 12px', marginBottom: 12, alignItems: 'center' }}>
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>廠商代號</span>
        <LookupField lookupType="vendor" value={formData.fn110_51 || ''} onChange={v => onVendorFieldChange('fn110_51', v)} disabled={isFieldDisabled('fn110_51')} maxLength={12} style={{ maxWidth: 140 }} />
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>幣別</span>
        <Input size="small" disabled={isFieldDisabled('fn110_52')} value={formData.fn110_52 || ''} onChange={e => onVendorFieldChange('fn110_52', e.target.value)} style={{ maxWidth: 100 }} />
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>匯率</span>
        <InputNumber size="small" disabled={isFieldDisabled('fn110_53')} value={formData.fn110_53 || 0} onChange={v => onVendorFieldChange('fn110_53', v || 0)} style={{ maxWidth: 100 }} />
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>採購人員</span>
        <LookupField lookupType="employee" value={formData.fn110_59 || ''} onChange={v => onVendorFieldChange('fn110_59', v)} disabled={isFieldDisabled('fn110_59')} maxLength={12} style={{ maxWidth: 120 }} />
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>貨款抵扣</span>
        <InputNumber size="small" disabled={isFieldDisabled('fn110_54')} value={formData.fn110_54 || 0} onChange={v => onVendorFieldChange('fn110_54', v || 0)} style={{ maxWidth: 120 }} />
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>抵扣類型</span>
        <Select size="small" disabled={isFieldDisabled('fn110_551')} value={formData.fn110_551 || undefined} onChange={v => onVendorFieldChange('fn110_551', v)} style={{ maxWidth: 160 }} allowClear
          options={[{ value: '1', label: '庫位移轉單' }, { value: '2', label: '出庫通知單' }, { value: '3', label: '客訴買收交運單' }, { value: '4', label: '無單據' }]} />
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>進貨退款</span>
        <InputNumber size="small" disabled={isFieldDisabled('fn110_55')} value={formData.fn110_55 || 0} onChange={v => onVendorFieldChange('fn110_55', v || 0)} style={{ maxWidth: 120 }} />
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>退款</span>
        <InputNumber size="small" disabled={isFieldDisabled('fn110_56')} value={formData.fn110_56 || 0} onChange={v => onVendorFieldChange('fn110_56', v || 0)} style={{ maxWidth: 120 }} />
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>買收單號</span>
        <Input size="small" disabled={isFieldDisabled('fn110_552')} value={formData.fn110_552 || ''} onChange={e => onVendorFieldChange('fn110_552', e.target.value)} style={{ maxWidth: 140 }} />
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>繳庫退回類別</span>
        <Radio.Group disabled={isFieldDisabled('fn110_561')} value={formData.fn110_561 || undefined} onChange={e => onVendorFieldChange('fn110_561', e.target.value)} size="small">
          <Radio value="1">繳庫退回單</Radio>
          <Radio value="2">出庫通知單</Radio>
        </Radio.Group>
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>退款參考單號</span>
        <Input size="small" disabled={isFieldDisabled('fn110_562')} value={formData.fn110_562 || ''} onChange={e => onVendorFieldChange('fn110_562', e.target.value)} style={{ maxWidth: 140 }} />
        <span style={{ textAlign: 'right', color: 'rgba(0,0,0,0.65)', fontSize: 13 }}>廠商備註</span>
        <Input.TextArea size="small" disabled={isFieldDisabled('fn110_57')} value={formData.fn110_57 || ''} onChange={e => onVendorFieldChange('fn110_57', e.target.value)} rows={2} style={{ gridColumn: 'span 3', maxWidth: 400 }} />
      </div>

      {isEditing && (
        <Space style={{ marginBottom: 8 }}>
          <Button size="small" type="primary" icon={<PlusOutlined />} onClick={handleAdd}>新增原因</Button>
        </Space>
      )}
      <Table<Partial<FN1122>>
        columns={columns}
        dataSource={rows}
        rowKey={(_r, i) => String(i)}
        size="small"
        pagination={false}
        bordered
        scroll={{ y: 300 }}
      />
    </div>
  );
};

export default React.memo(Tab3Vendor2Details);
